import React, { CSSProperties, FC } from 'react';
import { useFormContext } from 'react-hook-form';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';

import FormLabel from './FormLabel';
import FormControl from './FormControl';
import { IFormControlProps } from './types';

interface IFormGroupProps extends IFormControlProps {
  controlId: string;
  label?: string;
  hint?: string;
  hintClass?: string;
  tooltip?: string;
  tooltipPosition?: 'left' | 'right';
  children?: React.ReactNode;
  isHorizontal?: boolean;
  isRowGroup?: boolean;
  formGroupClassName?: string;
  onChange?: (val: any) => void;
  style?: CSSProperties;
  uploadUri?: string;
  deleteFileUri?: string;
  labelGrid?: number;
  controlGrid?: number;
  disableLocalization?: boolean;
  hasOptionTooltip?: boolean;
  yearDropdownItemNumber?: number;
  scrollableYearDropdown?: boolean;
  dropdownMode?: 'scroll' | 'select' | undefined;
  maxLength?: number;
  inputType?: string;
  limitOptions?: boolean;
  filterDate?: (val: any) => boolean;
  isFileUploading?: (bool: boolean) => void;
  initialUploadedFiles?: any[];
  disableFilterOption?: boolean;
  onMenuClose?: () => void;
  isWholeNumber?: boolean;
  showTimeSelectOnly?: boolean;
  timeIntervals?: number;
}

/*
 * Abstracted form group component, which includes:
 * - label (optional)
 * - control/field (input, select, etc.)
 * - hint (optional)
 * - hintClass (optional)
 * - field errors (if any)
 *
 * It serves different layout based on props.
 */
const FormGroup: FC<IFormGroupProps> = ({
  controlId,
  label,
  isRowGroup,
  type,
  checkType,
  as,
  placeholder,
  name,
  min,
  max,
  autoComplete,
  dateFormat,
  isDateRange,
  startDate,
  endDate,
  minDate,
  maxDate,
  filterDate,
  showYearPicker,
  showTimeSelect,
  showMonthDropdown,
  showYearDropdown,
  showMonthYearPicker,
  isRequired,
  isDisabled,
  isClearable,
  isCreatable,
  isSearchable,
  isPassword,
  isConfirmPassword,
  isVerificationCode,
  isEGN,
  isIdCard,
  isNumber,
  isLeaveDaysCount,
  hint,
  hintClass,
  tooltip,
  tooltipPosition,
  children,
  isHorizontal,
  options,
  noOptionsMessage,
  createMessage,
  disableOptions,
  selectOptions,
  loadOptions,
  isMulti,
  showTotal,
  allowSelectAll,
  multiple,
  accept,
  isImport,
  formGroupClassName,
  style,
  onChange,
  uploadUri,
  deleteFileUri,
  onFilesUploaded,
  onFilesDeleted,
  reset,
  labelGrid,
  controlGrid,
  setRef,
  disableLocalization,
  hasOptionTooltip,
  yearDropdownItemNumber,
  scrollableYearDropdown,
  dropdownMode,
  maxLength,
  inputType,
  isFileUploading,
  initialUploadedFiles,
  limitOptions,
  isAsyncSelect,
  disableFilterOption,
  onMenuClose,
  isWholeNumber,
  showTimeSelectOnly,
  timeIntervals,
  rows,
  testId,
}) => {
  const {
    formState: { errors },
  } = useFormContext();

  const renderControl = () => {
    return (
      <>
        <FormControl
          type={type}
          checkType={checkType}
          as={as}
          placeholder={placeholder}
          name={name}
          min={min}
          max={max}
          autoComplete={autoComplete}
          dateFormat={dateFormat}
          isDateRange={isDateRange}
          startDate={startDate}
          endDate={endDate}
          minDate={minDate}
          maxDate={maxDate}
          filterDate={filterDate}
          showYearPicker={showYearPicker}
          showTimeSelect={showTimeSelect}
          showMonthDropdown={showMonthDropdown}
          showYearDropdown={showYearDropdown}
          showMonthYearPicker={showMonthYearPicker}
          isRequired={isRequired}
          isDisabled={isDisabled}
          isPassword={isPassword}
          isConfirmPassword={isConfirmPassword}
          isVerificationCode={isVerificationCode}
          isEGN={isEGN}
          isIdCard={isIdCard}
          isNumber={isNumber}
          isLeaveDaysCount={isLeaveDaysCount}
          options={options}
          noOptionsMessage={noOptionsMessage}
          createMessage={createMessage}
          disableOptions={disableOptions}
          selectOptions={selectOptions}
          loadOptions={loadOptions}
          isCreatable={isCreatable}
          isClearable={isClearable}
          isSearchable={isSearchable}
          isMulti={isMulti}
          showTotal={showTotal}
          allowSelectAll={allowSelectAll}
          multiple={multiple}
          accept={accept}
          isImport={isImport}
          onChange={onChange}
          uploadUri={uploadUri}
          deleteFileUri={deleteFileUri}
          onFilesUploaded={onFilesUploaded}
          onFilesDeleted={onFilesDeleted}
          reset={reset}
          setRef={setRef}
          disableLocalization={disableLocalization}
          hasOptionTooltip={hasOptionTooltip}
          yearDropdownItemNumber={yearDropdownItemNumber}
          scrollableYearDropdown={scrollableYearDropdown}
          dropdownMode={dropdownMode}
          maxLength={maxLength}
          inputType={inputType}
          isFileUploading={isFileUploading}
          initialUploadedFiles={initialUploadedFiles}
          limitOptions={limitOptions}
          isAsyncSelect={isAsyncSelect}
          disableFilterOption={disableFilterOption}
          onMenuClose={onMenuClose}
          isWholeNumber={isWholeNumber}
          showTimeSelectOnly={showTimeSelectOnly}
          timeIntervals={timeIntervals}
          rows={rows}
          testId={testId}
        />
        {hint && (
          <Form.Text
            muted
            className={hintClass}
          >
            {hint}
          </Form.Text>
        )}
        {errors[name]?.message ? <Form.Text className="text-error">{errors[name]?.message as string}</Form.Text> : null}
      </>
    );
  };

  if (isRowGroup)
    return (
      <Col sm={6}>
        <Form.Group
          as={Row}
          controlId={controlId}
          data-testid={testId}
        >
          {label && (
            <FormLabel
              name={name}
              label={label}
              isRequired={isRequired}
              tooltip={tooltip}
              tooltipPosition={tooltipPosition}
              labelGrid={labelGrid}
            />
          )}
          <Col>
            {renderControl()}
            {children}
          </Col>
        </Form.Group>
      </Col>
    );

  const isEditorLayout = type === 'wysiwyg-editor' ? 9 : 6;
  return (
    <Form.Group
      as={Row}
      controlId={controlId}
      className={formGroupClassName}
      style={style}
      data-testid={testId}
    >
      {label && (
        <FormLabel
          name={name}
          label={label}
          isRequired={isRequired}
          isHorizontal={isHorizontal}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          labelGrid={labelGrid}
        />
      )}
      {/* <Col sm={controlGrid || (isHorizontal ? isEditorLayout : 12)}>{renderControl()}</Col> */}
      <Col lg={controlGrid || (isHorizontal ? isEditorLayout : 12)}>{renderControl()}</Col>
      {children}
    </Form.Group>
  );
};

export default FormGroup;
